import { useQuery } from "@apollo/client";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import {
  GetAccountQuery,
  GetAccountQueryVariables,
} from "types/graphql-schema";

import Container from "@components/container/container";
import GraphqlError from "@components/error/graphql-error";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { assertNonNull } from "@helpers/helpers";

import DirectorySync from "./components/directorysync";
import Email from "./components/email";
import Export from "./components/export";
import Integrations from "./components/integrations";
import AccountMeetings from "./components/meetings";
import Preferences from "./components/preferences";
import Profile from "./components/profile/profile";
import getAccountQuery from "./graphql/get-account-query";

const Account = () => {
  useDocumentTitle("Account settings");
  const { path } = useRouteMatch();
  const { loading, error, data, refetch } = useQuery<
    GetAccountQuery,
    GetAccountQueryVariables
  >(getAccountQuery, {
    fetchPolicy: "network-only",
  });

  return (
    <Container ariaLabel="Account" maxWidth="lg" padding="settings">
      {error ? (
        <GraphqlError
          error={error}
          whatDidNotWork="Your account could not be loaded."
        />
      ) : loading ? (
        <Loading />
      ) : data ? (
        <div className="text-gray-800">
          <Switch>
            <Route exact path={path}>
              <Profile me={data.me} refetch={refetch} />
            </Route>
            <Route exact path={`${path}/profile`}>
              <Profile me={data.me} refetch={refetch} />
            </Route>
            <Route exact path={`${path}/email`}>
              <Email me={assertNonNull(data.me)} />
            </Route>
            <Route exact path={`${path}/meetings`}>
              <AccountMeetings me={assertNonNull(data.me)} />
            </Route>
            <Route exact path={`${path}/integrations`}>
              <Integrations me={data.me} />
            </Route>
            <Route exact path={`${path}/calendar`}>
              <Redirect to="/account/profile" />
            </Route>
            <Route exact path={`${path}/preferences`}>
              <Preferences me={data.me} />
            </Route>
            <Route exact path={`${path}/export`}>
              <Export />
            </Route>
            <Route exact path={`${path}/directorysync`}>
              <DirectorySync me={data.me} />
            </Route>
          </Switch>
        </div>
      ) : (
        <GraphqlError
          whatDidNotWork="Your account could not be loaded."
          error={new Error("Account could not be loaded")}
        />
      )}
    </Container>
  );
};

export default Account;
